/*
 *   Copyright (c) 2021 vlinder Inc.
 *   All rights reserved.
 *   This software is proprietary of vlinder Inc. and can not be distributed / used for any purpose outside of vlinder Inc. without explicit written consent from the copyright owner.
 */

import React from 'react';
import * as S from './styles';
import * as V from './animation.variants';
import { ToggleAction } from '../ToggleDropDown';
import RenderWhen from '../../When';
import { Row, Col, Container } from 'react-bootstrap';
import { PlaceHolderComp } from './components/placeholder';

const DEFAULT_BG_IMAGE =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.widgets/white-bg.jpg';
const AUDIO_ICON = 'https://i.ibb.co/r0MbSZ0/music-button.png';
const THREED_ICON = `https://i.ibb.co/5xtFmp6/3d-button.png`;
const PDF_ICON = 'https://i.ibb.co/NmgKb1p/pdf-button.png';
const VIDEO_ICON = 'https://i.ibb.co/86dRKph/video-button.png';

interface IIconListProps {
  listItems?: {
    id: string;
    listImg?: string;
    title?: string | string[];
    meta?: string | string[];
    type?: 'video' | 'audio' | 'pdf' | '3d';
    metaLogo?: string;
  }[];
}

interface IIconListDataProps {
  data?:
    | {
        title?: string;
        listItems: IIconListProps;
        id: string;
        viewAllText?: string;
      }[]
    | IIconListProps;
  isLoading?: boolean;
  type?: 'icon' | 'media' | 'icon-sections' | 'media-sections';
  metaSymbol?: string;
  titleSymbol?: string;
  onListClick?(data?: any): void;
  onViewAllClick?(key: string): void;
  actionMenu?: {
    key: string;
    label: string;
  }[];
  onActionClick?(key: string, listId: string): void;
}

const getFirstLetterOfEachWord = (text: any) =>
  text?.match(/\b\w/g)?.join('')?.substr(0, 2)?.toUpperCase();

export const MediaListComp = (props: IIconListDataProps) => {
  const {
    data,
    isLoading,
    actionMenu,
    titleSymbol,
    metaSymbol,
    type,
    onListClick,
    onActionClick,
    onViewAllClick,
  } = props;

  const onListItemClick = (ele: any) => {
    onListClick && onListClick(ele);
  };

  const onToggleMenuClick = (key: string, id: string) => {
    onActionClick && onActionClick(key, id);
  };

  const _getTitle = (title?: string | string[]) => {
    if (Array.isArray(title) && title?.length > 0) {
      return (
        <>
          {title?.map((ele, index) => {
            if (index === title.length - 1) {
              return <span key={`${ele}-${index}`}>{ele}</span>;
            }
            return (
              <span key={`${ele}-${index}`}>
                {ele} {titleSymbol}{' '}
              </span>
            );
          })}
        </>
      );
    } else return title;
  };

  const _getMeta = (title?: string | string[], metaLogo?: string) => {
    if (Array.isArray(title) && title?.length > 0) {
      return (
        <>
          {title?.map((ele, index) => {
            if (index === title.length - 1) {
              return (
                <span key={`${ele}-${index}`}>
                  {index === 0 && !!metaLogo && (
                    <S.MetaLogo
                      src={metaLogo}
                      className="avatar rounded me-1"
                    />
                  )}
                  {ele}
                </span>
              );
            }
            return (
              <span key={`${ele}-${index}`}>
                {index === 0 && !!metaLogo && (
                  <S.MetaLogo src={metaLogo} className="avatar rounded me-1" />
                )}
                {ele} {metaSymbol}{' '}
              </span>
            );
          })}
        </>
      );
    } else return title;
  };

  const _getListImg = (element: any) => {
    if (element?.listImg) {
      return <S.ItemImagePlaceholder src={element?.listImg} />;
    } else {
      return (
        <S.SpanContainer className="avatar align-items-center justify-content-center fw-bold rounded align-self-center p-4">
          <span className="fs-2">
            {getFirstLetterOfEachWord(
              Array.isArray(element?.title) ? element?.title[0] : element?.title
            )}
          </span>
        </S.SpanContainer>
      );
    }
  };

  const _getTypeIcon = (type: string) => {
    switch (type) {
      case 'audio':
        return (
          <S.IconImage src={AUDIO_ICON} roundedCircle width={25} height={25} />
        );
      case 'video':
        return (
          <S.IconImage src={VIDEO_ICON} roundedCircle width={25} height={25} />
        );
      case 'pdf':
        return (
          <S.IconImage src={PDF_ICON} roundedCircle width={25} height={25} />
        );
      case '3d':
        return (
          <S.IconImage src={THREED_ICON} roundedCircle width={25} height={25} />
        );
      default:
        return null;
    }
  };

  const _getIconList = (list: any) => {
    let _listItems: any = [];
    if (Array.isArray(list) && list?.length > 0) {
      list?.forEach((element, index) => {
        _listItems.push(
          <Col xs={12} sm={4} md={4} lg={3}>
            <S.ListContainer
              whileHover={'whileHover'}
              transition={V.CardContainerVariant.transition}
              variants={V.CardContainerVariant}
              key={`${element?.id}-${index}`}
              onClick={() => onListItemClick(element)}
            >
              <S.ItemImageContainer
                backgroundImage={element?.listImg ?? DEFAULT_BG_IMAGE}
              >
                <S.ItemImage src={element?.listImg ?? DEFAULT_BG_IMAGE}>
                  {_getListImg(element)}
                </S.ItemImage>
                {_getTypeIcon(element?.type)}
              </S.ItemImageContainer>
              <div>
                <S.TitleContainer>
                  <S.Title className="fw-bold p-2">
                    {_getTitle(element.title)}{' '}
                  </S.Title>
                  {Array.isArray(actionMenu) && actionMenu?.length > 0 ? (
                    <S.ToggleContainer className="toggle-menu">
                      <ToggleAction
                        actionMenu={actionMenu}
                        onActionClick={(key) =>
                          onToggleMenuClick(key, element?.id)
                        }
                      />
                    </S.ToggleContainer>
                  ) : null}
                </S.TitleContainer>
                <S.Meta className="m-0 px-2">
                  {_getMeta(element?.meta, element?.metaLogo)}
                </S.Meta>
              </div>
            </S.ListContainer>
          </Col>
        );
      });
    }
    return _listItems;
  };

  const _getSectionDivider = (index: number) => {
    if (Array.isArray(data) && data?.length - 1 !== index) {
      return <S.SectionDivider />;
    }
    return null;
  };

  const _getDataList = () => {
    const _dataList: any = [];
    if (isLoading) {
      return <PlaceHolderComp />;
    }
    if (type === 'media' && Array.isArray(data) && data?.length > 0) {
      return <Row>{_getIconList(data)}</Row>;
    }
    if (Array.isArray(data) && data?.length > 0) {
      data?.forEach((element, index) => {
        _dataList.push(
          <>
            <Row key={`${element?.title}-${index}`}>
              <Col>
                <RenderWhen>
                  <RenderWhen.If isTrue={!!element?.title}>
                    <S.SectionTitle className="ms-4">
                      {element?.title}{' '}
                      <S.ViewAllContainer className="fw-normal float-end text-secondary">
                        <S.SeeAll
                          onClick={() =>
                            onViewAllClick && onViewAllClick(element?.id)
                          }
                        >
                          {' '}
                          {element?.viewAllText ?? 'See All'}
                        </S.SeeAll>
                      </S.ViewAllContainer>
                    </S.SectionTitle>
                  </RenderWhen.If>
                </RenderWhen>
              </Col>
            </Row>
            <Row>{_getIconList(element?.listItems)}</Row>
            <Row>
              <Col>{_getSectionDivider(index)}</Col>
            </Row>
          </>
        );
      });
      return _dataList;
    }
    return null;
  };

  return (
    <Container fluid className="m-0 p-0">
      {_getDataList()}
    </Container>
  );
};

MediaListComp.defaultProps = {
  metaSymbol: '•',
  titleSymbol: '|',
};
